import { useState, useEffect, useCallback } from "react";
import { axios_tenant } from "../../../../axios";

const BASE_URL = "/collaboration/workflow";

/**
 * Shared hook for fetching a single task with all related data.
 * @param {string} taskId - The OAT-YYYY-NNNN task ID.
 */
const useTaskDetail = (taskId) => {
    const [task, setTask] = useState(null);
    const [packages, setPackages] = useState([]);
    const [observations, setObservations] = useState([]);
    const [audits, setAudits] = useState([]);
    const [permittedTransitions, setPermittedTransitions] = useState([]);
    const [submitTransitions, setSubmitTransitions] = useState([]);
    const [allowedActions, setAllowedActions] = useState([]);
    const [canAdminAssign, setCanAdminAssign] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTask = useCallback(async (background = false) => {
        if (!taskId) return;
        if (!background) setLoading(true);
        try {
            const [taskRes, pkgRes, obsRes] = await Promise.all([
                axios_tenant.get(`${BASE_URL}/tasks/${taskId}`),
                axios_tenant.get(`${BASE_URL}/tasks/${taskId}/package`),
                axios_tenant.get(`${BASE_URL}/tasks/${taskId}/observations`)
            ]);

            const taskData = taskRes.data.data;
            const enrichedTask = taskData ? {
                ...taskData,
                canAdminAssign: taskRes.data.canAdminAssign || false,
                submitTransitions: taskRes.data.submitTransitions || [],
                allowedActions: taskRes.data.allowedActions || [],
                permittedTransitions: taskRes.data.permittedTransitions || taskRes.data.dropdownTransitions || []
            } : null;
            setTask(enrichedTask);
            setPermittedTransitions(taskRes.data.permittedTransitions || taskRes.data.dropdownTransitions || []);
            setSubmitTransitions(taskRes.data.submitTransitions || []);
            setAllowedActions(taskRes.data.allowedActions || []);
            setCanAdminAssign(taskRes.data.canAdminAssign || false);
            setPackages(pkgRes.data.data || []);
            setObservations(obsRes.data.data || []);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load task");
        } finally {
            if (!background) setLoading(false);
        }
    }, [taskId]);

    const fetchObservations = useCallback(async () => {
        if (!taskId) return;
        try {
            const obsRes = await axios_tenant.get(`${BASE_URL}/tasks/${taskId}/observations`);
            setObservations(obsRes.data.data || []);
        } catch (err) {
            console.error("Failed to refetch observations:", err);
        }
    }, [taskId]);

    useEffect(() => {
        fetchTask(false);
    }, [fetchTask]);

    return {
        task, packages, observations, audits: [],
        permittedTransitions, submitTransitions, allowedActions, canAdminAssign,
        loading, error, refetch: (background = true) => fetchTask(background), refetchObservations: fetchObservations
    };
};

export default useTaskDetail;
